import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { View, ScrollView, StyleSheet } from "react-native";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import { DownArrow } from "../../../components/svgs/common";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import ProcessingMessageBox from "../../../components/chat/LoadingMessage/ProcessingMessageBox";
import { AITutorMessageBubble } from "../../../components/chatNewInterface/interfaceComponents/messageBubble/AITutorMessageBubble";
import { ObjectiveTransitionCard } from "../../../components/chatNewInterface/interfaceComponents/inlineCards/ObjectiveTransitionCard";
import { AttachmentChipProps } from "../../../components/chatNewInterface/interfaceComponents/attachmentChip/AttachmentChip";
import type { MessageItem, ObjectiveTransitionEvent } from "../aiTutorMessage.types";
import { spacing } from "../../../styles/design-system";
import { type PlaybackSpeed } from "../../../utils/toolPreferenceStorage";
import { useAITutorMessagesReadAloud, type MessageReadAloudTtsDeps } from "./useAITutorMessagesReadAloud";
import type { MessageVisualizationBlock } from "../../../components/chatNewInterface/interfaceComponents/visualizationModal/VisualizationModal";

export type { MessageReadAloudTtsDeps };

const AITUTOR_MESSAGES_MAX_WIDTH = 704;

interface AITutorMessagesContainerProps {
  resourceId: string;
  messages: MessageItem[];
  transitions?: ObjectiveTransitionEvent[];
  isStreaming?: boolean;
  currentStreamingMessageId?: string | null;
  resetAutoScroll?: number;
  shouldRevealFirstMessageAtTop?: boolean;
  onRevealedFirstMessageAtTop?: () => void;
  feedbackByMessageId?: Record<string, "up" | "down">;
  onFeedback?: (messageId: string, value: "up" | "down" | null) => void;
  messageReadAloudTts: MessageReadAloudTtsDeps;
  autoReadAloudEnabled?: boolean;
  autoReadAloudMessageId?: string | null;
  readAloudMessageIdRemapEvent?: { fromMessageId: string; toMessageId: string; nonce: number } | null;
  playbackSpeed?: PlaybackSpeed;
  isScreenFocused?: boolean;
  onOpenVisualization?: (block: MessageVisualizationBlock) => void;
  onChoiceSelect?: (messageId: string, blockIndex: number, choiceIndex: number) => void;
}

const mapMessageToBubble = (message: MessageItem) => ({
  role: (message?.sender === "human" ? "user" : "assistant") as "user" | "assistant",
  content: message?.content ?? "",
  timestamp: message?.created ? new Date(message.created) : new Date(),
  isArchived: false,
});

const AITutorMessagesContainer = ({
  resourceId,
  messages,
  transitions = [],
  isStreaming = false,
  currentStreamingMessageId,
  resetAutoScroll,
  shouldRevealFirstMessageAtTop,
  onRevealedFirstMessageAtTop,
  feedbackByMessageId,
  onFeedback,
  messageReadAloudTts,
  autoReadAloudEnabled = true,
  autoReadAloudMessageId = null,
  readAloudMessageIdRemapEvent = null,
  playbackSpeed = 1,
  isScreenFocused = true,
  onOpenVisualization,
  onChoiceSelect,
}: AITutorMessagesContainerProps) => {
  const scrollViewRef = useRef<ScrollView | null>(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [downButton, setDownButton] = useState(false);
  const [humanMessageOffset, setHumanMessageOffset] = useState(0);
  const [scrollViewHeight, setScrollViewHeight] = useState<number | undefined>(undefined);
  const [parentHeight, setParentHeight] = useState(0);
  const [isUserInteracted, setIsUserInteracted] = useState(false);
  const isAtBottomRef = useRef(true);
  const isProgrammaticScrollRef = useRef(false);
  const clearUserInteractionOnBlur = useCallback(() => setIsUserInteracted(false), []);

  const transitionsByAnchor = useMemo(() => {
    const map = new Map<string, ObjectiveTransitionEvent[]>();
    for (const t of transitions) {
      if (!t.afterMessageId) continue;
      const list = map.get(t.afterMessageId);
      if (list) list.push(t);
      else map.set(t.afterMessageId, [t]);
    }
    return map;
  }, [transitions]);

  const { getAssistantReadAloudControl } = useAITutorMessagesReadAloud({
    resourceId,
    messages,
    messageReadAloudTts,
    autoReadAloudEnabled,
    autoReadAloudMessageId,
    readAloudMessageIdRemapEvent,
    playbackSpeed,
    isScreenFocused,
    onUserFocusCleanup: clearUserInteractionOnBlur,
  });

  const handleScroll = useCallback(
    (event: {
      nativeEvent: {
        layoutMeasurement: { height: number };
        contentOffset: { y: number };
        contentSize: { height: number };
      };
    }) => {
      const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
      const isAtBottom = layoutMeasurement.height + contentOffset.y >= contentSize.height - 50;
      if (isProgrammaticScrollRef.current) {
        if (isAtBottom) {
          setIsUserScrolling(false);
          setDownButton(false);
        }
        isAtBottomRef.current = isAtBottom;
        return;
      }
      if (!isAtBottom) {
        setIsUserScrolling(true);
        setDownButton(true);
        setIsUserInteracted(true);
      } else {
        setIsUserScrolling(false);
        setDownButton(false);
      }
      isAtBottomRef.current = isAtBottom;
    },
    []
  );

  const scrollToBottom = (animated = false) => {
    if (shouldRevealFirstMessageAtTop) {
      return;
    }
    isProgrammaticScrollRef.current = true;
    scrollViewRef.current?.scrollToEnd({ animated });
    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 0);
  };

  const scrollToBottomButton = () => {
    setDownButton(false);
    scrollToBottom(true);
  };

  const scrollToTop = useCallback((animated = true) => {
    isProgrammaticScrollRef.current = true;
    scrollViewRef.current?.scrollTo({ y: 0, animated });
    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 0);
  }, []);

  useEffect(() => {
    if (!shouldRevealFirstMessageAtTop) {
      return;
    }
    scrollToTop(true);
    const delayMs = 500;
    const timeoutId = setTimeout(() => {
      onRevealedFirstMessageAtTop?.();
    }, delayMs);
    return () => clearTimeout(timeoutId);
  }, [shouldRevealFirstMessageAtTop, scrollToTop, onRevealedFirstMessageAtTop]);

  useEffect(() => {
    const el = scrollViewRef.current as (ScrollView & { setAttribute?: (a: string, b: string) => void }) | null;
    if (el?.setAttribute) el.setAttribute("data-sensitive", "true");
  }, []);

  useEffect(() => {
    if (resetAutoScroll !== undefined) {
      setIsUserInteracted(false);
      setScrollViewHeight(undefined);
      setHumanMessageOffset(0);
    }
  }, [resetAutoScroll]);

  useEffect(() => {
    if (messages.length === 0) {
      setScrollViewHeight(undefined);
      setHumanMessageOffset(0);
    }
  }, [messages.length]);

  useEffect(() => {
    if (isUserInteracted && humanMessageOffset > 0 && !isUserScrolling) {
      isProgrammaticScrollRef.current = true;
      scrollViewRef.current?.scrollTo({ y: humanMessageOffset });
      setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, 0);
    } else {
      scrollToBottom();
    }
  }, [humanMessageOffset]);

  useEffect(() => {
    if (!messages?.length) return;
    if (!isUserInteracted) scrollToBottom();
  }, [messages]);

  const handleOnLayout = (
    event: { nativeEvent: { layout: { y: number } } },
    item: MessageItem,
    array: MessageItem[]
  ) => {
    const isLastHumanMessage = [...array].reverse().find((m) => m.sender === "human")?.id === item.id;
    if (isLastHumanMessage && parentHeight > 0) {
      const y = event.nativeEvent.layout.y;
      setHumanMessageOffset(y);
      setScrollViewHeight(y + parentHeight);
    }
  };

  useEffect(() => {
    if (humanMessageOffset > 0 && parentHeight > 0) {
      setScrollViewHeight(humanMessageOffset + parentHeight);
    }
  }, [humanMessageOffset, parentHeight]);

  const streamingMessage = currentStreamingMessageId
    ? messages.find((m) => m.id === currentStreamingMessageId)
    : undefined;
  const shouldShowProcessingFallback =
    isStreaming && (!streamingMessage || (!streamingMessage.content && !streamingMessage.blocks?.length));

  const visibleMessages = messages?.filter((item) => {
    if (
      isStreaming &&
      item.id === currentStreamingMessageId &&
      item.sender === "ai" &&
      !item.content?.trim() &&
      !item.blocks?.length
    ) {
      return false;
    }
    return true;
  });

  return (
    <View
      style={styles.outerContainer}
      onLayout={(e) => setParentHeight(e.nativeEvent.layout.height)}
      role="log"
      aria-live="polite">
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        maintainVisibleContentPosition={{ minIndexForVisible: 0, autoscrollToTopThreshold: 10 }}
        showsVerticalScrollIndicator
        contentContainerStyle={[
          styles.scrollContent,
          parentHeight > 0 && { minHeight: parentHeight },
          scrollViewHeight !== undefined && { height: scrollViewHeight },
        ]}
        onContentSizeChange={() => {
          if (!isUserInteracted) scrollToBottom();
        }}
        tabIndex={0}>
        <View style={styles.contentWrapper}>
          <View style={styles.messagesColumn}>
            {visibleMessages?.map((item, index, array) => {
              const transitionsAfterThis = transitionsByAnchor.get(item.id) ?? [];
              const attachmentItems: AttachmentChipProps[] = [];
              const isAssistantMessage = item.sender === "ai";
              const isStreamingCurrentMessage =
                isAssistantMessage && item.id === currentStreamingMessageId && isStreaming;
              const hasLaterHumanMessage = array.slice(index + 1).some((msg) => msg.sender === "human");
              // If the student already replied (later human message exists), mark any
              // unanswered inline_response cards as submitted so they render as
              // disabled/dimmed — keeping the question visible for context.
              const blocksForRender =
                hasLaterHumanMessage && item.blocks?.length
                  ? item.blocks.map((block) =>
                      block.kind === "inline_response" && !block.submitted ? { ...block, submitted: true } : block
                    )
                  : item.blocks;
              const hideReadAloudWhileStreaming = isStreamingCurrentMessage && autoReadAloudMessageId !== item.id;
              const readAloudControl = isAssistantMessage
                ? getAssistantReadAloudControl(item.id, item.content ?? "")
                : undefined;
              for (const todo of item.todos ?? []) {
                attachmentItems.push({ type: "task", name: todo.name ?? "", duedate: todo.date });
              }
              for (const assignment of item.assignments ?? []) {
                attachmentItems.push({
                  type: "assignment",
                  name: assignment.name ?? "",
                  duedate: assignment.due_date,
                });
              }
              for (const file of item.files ?? []) {
                attachmentItems.push({ type: "file", name: file.fileName ?? "" });
              }
              return (
                <React.Fragment key={String(item.id ?? index)}>
                  <View onLayout={(e) => handleOnLayout(e, item, array)}>
                    <AITutorMessageBubble
                      {...mapMessageToBubble(item)}
                      isStreaming={isStreamingCurrentMessage}
                      taskAttachmentChips={attachmentItems}
                      isFullWidth
                      feedback={item.sender === "ai" ? feedbackByMessageId?.[item.id] : undefined}
                      onFeedback={
                        item.sender === "ai" && item.id && onFeedback
                          ? (value) => onFeedback(item.id, value)
                          : undefined
                      }
                      hideReadAloudControl={hideReadAloudWhileStreaming}
                      readAloudControl={readAloudControl}
                      embedYouTube={!isStreamingCurrentMessage}
                      blocks={blocksForRender}
                      onChoiceSelect={onChoiceSelect ? (bi, ci) => onChoiceSelect(item.id, bi, ci) : undefined}
                      onOpenVisualization={onOpenVisualization}
                    />
                  </View>
                  {transitionsAfterThis.map((t) => (
                    <ObjectiveTransitionCard key={t.id} transition={t.transition} />
                  ))}
                </React.Fragment>
              );
            })}
            {shouldShowProcessingFallback && (
              <View>
                <CustomSpacing type="vertical" size="s" />
                <ProcessingMessageBox />
              </View>
            )}
          </View>
        </View>
      </ScrollView>
      {downButton && (
        <View style={styles.buttonContainer}>
          <CustomButton
            styleType="primary"
            style={styles.button}
            leftIcon={<DownArrow />}
            onPress={scrollToBottomButton}
            aria-label="Scroll to bottom of messages"
            accessibilityLabel="Scroll to bottom of messages"
            accessibilityRole="button"
            rightIcon={undefined}
            text={undefined}
            textProps={undefined}
            textStyle={undefined}
            disabled={false}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    width: "100%",
    paddingHorizontal: 0,
  },
  scrollView: {
    flex: 1,
    width: "100%",
    zIndex: 0,
    paddingHorizontal: spacing.lg,
  },
  scrollContent: {
    paddingTop: 20,
    paddingHorizontal: 0,
  },
  contentWrapper: {
    width: "100%",
    alignItems: "center",
    paddingHorizontal: 0,
  },
  messagesColumn: {
    width: "100%",
    maxWidth: AITUTOR_MESSAGES_MAX_WIDTH,
    paddingHorizontal: 0,
    paddingBottom: 40,
  },
  buttonContainer: {
    width: "100%",
    alignItems: "center",
  },
  button: {
    justifyContent: "center",
    position: "absolute",
    alignItems: "center",
    bottom: 10,
    width: 30,
    height: 30,
    borderRadius: 25,
    zIndex: 5,
  },
});

export default AITutorMessagesContainer;
